// Aktivite akışı — audit eylem kodlarının Türkçe cümleleri TEK kaynak (Finar UX P2 İ2).
// AdminDashboard "Son aktivite" + ProjectDetail proje kaydı aynı haritayı kullanır.
// Ayarlar → Audit Log süzgeci teknik kodları bilerek ham gösterir (arama/filtre aracı).

import { type AuditAction } from "@mahir/shared";

export const ACTIVITY_COPY = {
  title: "Son aktivite",
  projectTitle: "Proje kaydı",
  systemActor: "Sistem",
  empty: "Bu proje için kayıt yok.",
  loading: "Yükleniyor…",
  loadFailed: "Kayıtlar yüklenemedi",
  /** Etiketi olmayan (eski) eylem kodu — ham kod yalnız title'da gösterilir. */
  unknownAction: "Sistem kaydı",
  shownOf: (shown: number, total: number): string =>
    `Son ${shown.toString()} kayıt gösteriliyor · Toplam ${total.toString()}`,
  /** Toplu form kaydı — değişen alanlar (ilk N kod + kalan sayı). */
  changedFields: (codes: readonly string[], total: number): string => {
    const rest = total - codes.length;
    return rest > 0 ? `${codes.join(", ")} (+${rest.toString()})` : codes.join(", ");
  },
} as const;

// Tipli harita: shared AUDIT_ACTIONS'taki her kod etiketlenmek ZORUNDA (eksik anahtar
// derleme hatası — yeni eylem etiketsiz yayına çıkamaz).
export const ACTION_LABELS: Record<AuditAction, string> = {
  "auth.login.success": "Oturum açtı",
  "auth.login.failure": "Başarısız giriş denemesi",
  "auth.login.locked": "Hesap geçici olarak kilitlendi",
  "auth.logout": "Oturumu kapattı",
  "auth.password_reset.requested": "Şifre sıfırlama istedi",
  "auth.password_reset.completed": "Şifresini yeniledi",
  "auth.first_login.completed": "Hesabını etkinleştirdi",
  "auth.session.invalidated": "Oturum sonlandırıldı",
  "framework.create": "Çerçeve oluşturdu",
  "framework.update": "Çerçeveyi güncelledi",
  "framework.version_fork": "Yeni çerçeve sürümü açtı",
  "framework.version_delete": "Çerçeve sürümünü sildi",
  "framework.publish": "Çerçeveyi yayımladı",
  "framework.section.create": "Çerçeveye bölüm ekledi",
  "framework.section.update": "Çerçeve bölümünü güncelledi",
  "framework.section.delete": "Çerçeve bölümünü sildi",
  "framework.question.create": "Çerçeveye soru ekledi",
  "framework.question.update": "Çerçeve sorusunu güncelledi",
  "framework.question.delete": "Çerçeve sorusunu sildi",
  "project.created": "Proje oluşturdu",
  "project.kpi_set.changed": "Projenin KPI veri setini değiştirdi",
  "form_response.batch_upsert": "Form yanıtlarını güncelledi",
  "form_response.stale_write_rejected": "Eşzamanlı yazım reddedildi",
  "settings.update": "Sistem ayarlarını güncelledi",
  "llm.test": "AI bağlantısını sınadı",
  "document.export": "Rapor indirdi",
  "section.content.update": "Bölüm metnini düzenledi",
  "section.review.approve": "Bölümü onayladı",
  "section.review.reject": "Bölümü reddetti",
  "section.review.reopen": "Bölüm onayını kaldırdı",
  "section.regenerate": "Bölümü yeniden ürettirdi",
  "section.regenerate.discarded": "Onaylı bölümün yeni metni atıldı",
  "review.kpi_stale_cleared": "KPI rakamlarını doğruladı",
  "project.reopen": "Projeyi yeniden açtı",
  "project.auto_complete": "Proje tamamlandı",
  "project.manual_complete": "Projeyi tamamladı",
  "project.archive": "Projeyi arşive aldı",
  "framework.delete": "Çerçeveyi sildi",
  "queue.paused": "İş kuyruğunu duraklattı",
  "queue.resumed": "İş kuyruğunu sürdürdü",
  "client.branding.update": "Müşteri markasını güncelledi",
  "client.branding.logo_update": "Müşteri logosunu güncelledi",
  "client.branding.logo_delete": "Müşteri logosunu kaldırdı",
  "client.branding.disable": "Müşteri markasını kapattı",
  "settings.branding_default.update": "Varsayılan markayı güncelledi",
  "settings.branding_default.logo_update": "Varsayılan logoyu güncelledi",
  "settings.branding_default.logo_delete": "Varsayılan logoyu kaldırdı",
  "sector_package.create": "Sektör paketi oluşturdu",
  "sector_package.update": "Sektör paketini güncelledi",
  "sector_package.publish": "Sektör paketini yayımladı",
  "sector_package.archive": "Sektör paketini arşivledi",
  "sector_package.delete": "Sektör paketini sildi",
  "sector_package.upgrade": "Sektör paketini yükseltti",
  "sector_package.derived": "Sektör paketi türetildi",
  "framework.version.edit_published": "Yayındaki çerçeve sürümünü düzenledi",
  "framework.prompt.update": "Çerçeve yönergesini güncelledi",
  "framework.exported": "Çerçeve dışa aktardı",
  "framework.imported": "Çerçeve içe aktardı",
  "framework.version_imported": "Çerçeveye yeni sürüm içe aktardı",
  "section.qc.start": "Kalite kontrolü başlattı",
  "section.qc.accept": "Kalite kontrol önerisini kabul etti",
  "section.qc.reject": "Kalite kontrol önerisini reddetti",
  "section.qc.clear": "Kalite kontrol sonucunu temizledi",
  "qc_prompt.create": "Kalite kontrol yönergesi ekledi",
  "qc_prompt.update": "Kalite kontrol yönergesini güncelledi",
  "qc_prompt.delete": "Kalite kontrol yönergesini sildi",
  "project.participant.invited": "Müşteri katılımcı davet etti",
  "project.participant.submitted": "Müşteri formu gönderdi",
  "project.participant.reopened": "Müşteri formunu yeniden açtı",
  "project.participant.revoked": "Müşteri katılımcının erişimini kaldırdı",
  "report_import.batch_created": "Rapor aktarımı başlattı",
  "report_import.completed": "Rapor aktarımı tamamlandı",
  "report_import.failed": "Rapor aktarımı başarısız oldu",
  "report_import.retried": "Rapor aktarımını yeniden denedi",
  "report_import.cancelled": "Rapor aktarımını iptal etti",
  "report_import.archived": "Rapor aktarımını geri çekti",
  "report_import.requeued": "Rapor aktarımı yeniden kuyruğa alındı",
  "report_check.start": "Tutarlılık denetimi başlattı",
  "report_check.done": "Tutarlılık denetimi tamamlandı",
  "report_check.failed": "Tutarlılık denetimi başarısız oldu",
  "report_check.finding.resolve": "Tutarlılık bulgusunu giderildi işaretledi",
  "report_check.finding.dismiss": "Tutarlılık bulgusunu yok saydı",
  "report_check.finding.reopen": "Tutarlılık bulgusunu yeniden açtı",
  "client_entity.created": "İşletme ekledi",
  "client_entity.updated": "İşletmeyi güncelledi",
  "client_entity.deactivated": "İşletmeyi pasifleştirdi",
  "client_entity.deleted": "İşletmeyi sildi",
  "client_entity.values_moved": "İşletme değerlerini taşıdı",
  "client_admin.granted": "Müşteri yöneticisi atadı",
  "client_admin.revoked": "Müşteri yöneticiliğini kaldırdı",
  "client_admin.user_invited": "Müşteri kullanıcısı davet etti",
  "client_admin.user_deactivated": "Müşteri kullanıcısını pasifleştirdi",
  "client_admin.access_updated": "Müşteri erişimini güncelledi",
  "client_admin.role_changed": "Müşteri kullanıcısının rolünü değiştirdi",
  "client_admin.scope_updated": "Müşteri erişim kapsamını güncelledi",
  "kpi.access.granted": "KPI erişimi verdi",
  "kpi.access.override_set": "KPI erişim istisnası tanımladı",
  "evidence.purged": "Saklama süresi dolan kanıtlar silindi",
  "evidence.scan_infected": "Kanıt dosyası taramada zararlı bulundu",
  "client.evidence_policy.updated": "Müşteri kanıt politikasını güncelledi",
  "kpi.evidence.retention_updated": "Kanıt saklama süresini güncelledi",
  "kpi.metric.created": "Katalog göstergesi ekledi",
  "kpi.metric.updated": "Katalog göstergesini güncelledi",
  "kpi.metric.deprecated": "Katalog göstergesini kullanımdan kaldırdı",
  "kpi.metric.deleted": "Katalog göstergesini sildi",
  "kpi.metric.label_upserted": "Gösterge çeviri etiketini güncelledi",
  "kpi.metric.label_deleted": "Gösterge çeviri etiketini sildi",
  "kpi.metric.code_renamed": "Gösterge kodunu yeniden adlandırdı",
  "kpi.metric.reordered": "Katalog sırasını değiştirdi",
  "kpi.metric.promoted": "Müşteriye özel göstergeyi kataloğa yükseltti",
  "kpi.unit_changed": "Gösterge birimini değiştirdi",
  "kpi.package.created": "Sektör paketi oluşturdu",
  "kpi.package.updated": "Sektör paketini güncelledi",
  "kpi.package.archived": "Sektör paketini arşivledi",
  "kpi.package.unarchived": "Sektör paketini arşivden çıkardı",
  "kpi.package.items_updated": "Sektör paketinin göstergelerini güncelledi",
  "kpi.package.suggested": "Sektör paketi için AI önerisi aldı",
  "kpi.breakdown.metadata_changed": "Kırılım grubunun hesaplama kuralını değiştirdi",
  "kpi.breakdown.converted": "Göstergeyi kırılıma dönüştürdü",
  "kpi.breakdown.group_created": "Kırılım grubu oluşturdu",
  "kpi.breakdown.dimension_created": "Özel kırılım boyutu tanımladı",
  "kpi.breakdown.dimension_updated": "Özel kırılım boyutunu düzenledi",
  "kpi.breakdown.dimension_deleted": "Özel kırılım boyutunu sildi",
  "kpi.breakdown.created_by_import": "İçe aktarmada kırılım grubu/üyesi ekledi",
  "kpi.set.forked": "KPI veri seti oluşturdu",
  "kpi.set.updated": "KPI veri setini güncelledi",
  "kpi.set.deleted": "KPI veri setini sildi",
  "kpi.set.indicator_added": "KPI veri setine gösterge ekledi",
  "kpi.set.indicator_updated": "KPI veri seti göstergesini güncelledi",
  "kpi.set.indicator_removed": "KPI veri setinden gösterge çıkardı",
  "kpi.value.updated": "KPI değeri girdi",
  "kpi.value.verified": "KPI değerini onayladı",
  "kpi.value.client_approved": "Müşteri KPI değerini onayladı",
  "kpi.value.client_note": "Müşteri KPI değerine not ekledi",
  "kpi.value.clarification_requested": "KPI değeri için açıklama istedi",
  "kpi.indicator.checked": "Göstergeyi kontrol etti",
  "kpi.variant.requested": "Formül varyantı talep etti",
  "kpi.variant.verified": "Formül varyantını onayladı",
  "kpi.variant.rejected": "Formül varyantını reddetti",
  "kpi.variant.pending_discarded": "Bekleyen varyant talebi düştü",
  "kpi.import.created": "KPI içe aktarımı başlattı",
  "kpi.import.committed": "KPI içe aktarımını tamamladı",
  "kpi.import.deleted": "KPI içe aktarımını sildi",
  "kpi.evidence.uploaded": "Kanıt belgesi yükledi",
  "kpi.evidence.linked": "Kanıt belgesi bağladı",
  "kpi.evidence.unlinked": "Kanıt belgesi bağını kaldırdı",
  "kpi.ai_check.run": "KPI kontrolü çalıştırdı",
  "kpi.catalog.imported": "Kataloğa dosyadan gösterge aktardı",
  "kpi.target.upserted": "KPI hedefini güncelledi",
  "kpi.target.deleted": "KPI hedefini sildi",
};

function isKnownAction(action: string): action is AuditAction {
  return Object.prototype.hasOwnProperty.call(ACTION_LABELS, action);
}

/** Bilinmeyen kod (eski kayıt) ham gösterilmez: nötr metin; kod yalnız `title`'da. */
export function actionLabel(action: string): string {
  return isKnownAction(action) ? ACTION_LABELS[action] : ACTIVITY_COPY.unknownAction;
}

/**
 * Tek aktivite satırının cümlesi. `form_response.batch_upsert` metadata'sında
 * `question_codes` (ilk N) varsa hangi alanların değiştiği eklenir.
 */
export interface AuditEntryView {
  label: string;
  detail: string | null;
  /** Yalnız etiketsiz (eski) kodda dolu — `title` özniteliğine konur, metne değil. */
  rawCode: string | null;
}

export function describeAuditEntry(entry: { action: string; metadata?: unknown }): AuditEntryView {
  const label = actionLabel(entry.action);
  const rawCode = isKnownAction(entry.action) ? null : entry.action;
  const base = { label, rawCode };
  if (entry.action !== "form_response.batch_upsert") return { ...base, detail: null };
  const m = entry.metadata;
  if (m === null || typeof m !== "object") return { ...base, detail: null };
  const md = m as { question_codes?: unknown; question_code?: unknown; count?: unknown };
  if (Array.isArray(md.question_codes)) {
    const codes = md.question_codes.filter((c): c is string => typeof c === "string");
    if (codes.length === 0) return { ...base, detail: null };
    const total = typeof md.count === "number" ? Math.max(md.count, codes.length) : codes.length;
    return { ...base, detail: ACTIVITY_COPY.changedFields(codes, total) };
  }
  // Kanıt eki yazımı tek kod taşır (attachments.ts).
  if (typeof md.question_code === "string") return { ...base, detail: md.question_code };
  return { ...base, detail: null };
}
